import { Button, ButtonColors } from '@chiroup/components/Button';
import { Loading } from '@chiroup/components/Loading';
import {
  KnownClearingHouseSettings,
  KnownClearingHouses,
} from '@chiroup/core/types/BillingCode.type';
import { ListClinic } from '@chiroup/core/types/Clinic.type';
import React, { useState } from 'react';
import { useAdminPermissions } from '../../hooks/useAdminPermissions';
import useIntegrations from '../../hooks/useIntegrations';
import List from '../common/list/List';
import ListItem from '../common/list/ListItem';

interface Integration {
  id: number;
  name: string;
  appReceiverCode?: string;
  production?: boolean;
  type?: string;
}

interface IntegrationsProps {
  clinic: Partial<ListClinic>;
}

const Integrations: React.FC<IntegrationsProps> = ({ clinic }) => {
  const { data, isFetching, activateIntegration, saving } = useIntegrations(clinic.ID || -1);
  const { hasPermission } = useAdminPermissions();
  const [activatingIntegrationId, setActivatingIntegrationId] = useState<number | null>(null);

  const getIntegrationClearingHouseName = (integration: Integration): string => {
    const clearingHouseKey = Object.keys(KnownClearingHouseSettings).find(
      key =>
        KnownClearingHouseSettings[key as KnownClearingHouses]?.appReceiverCode ===
        integration.appReceiverCode
    );

    return clearingHouseKey || 'Unknown Clearing House';
  };

  if (isFetching) {
    return <Loading />;
  }

  return (
    <List isFetching={isFetching}>
      {data?.data?.map((integration: Integration, index: number) => {
        const integrationTypeDisplay = integration.type
          ? integration.type === 'edi'
            ? 'EDI'
            : 'Manual EDI'
          : '';

        const handleActivation = async () => {
          setActivatingIntegrationId(integration.id);
          await activateIntegration(
            integration.id,
            !(integration.production && integration.type === 'edi')
          );
          setActivatingIntegrationId(null);
        };

        return (
          <ListItem key={integration.id} index={index}>
            <div className="flex items-center justify-between space-x-4 ">
              <div className="min-w-0">
                <div className="flex items-center space-x-2">
                  <div className="text-sm font-medium text-gray-900">
                    {integration.name}{' '}
                    <span
                      className={
                        integration.production && integration.type === 'edi'
                          ? 'text-sm text-primary-500'
                          : 'text-sm text-gray-500'
                      }
                    >
                      {integration.production && integration.type === 'edi'
                        ? '(Active)'
                        : '(Inactive)'}
                    </span>
                  </div>
                </div>
                <span className="text-sm text-gray-500">
                  {getIntegrationClearingHouseName(integration)}
                  {integrationTypeDisplay && ` (${integrationTypeDisplay})`}
                </span>
              </div>
              {hasPermission('clinic.toggle_integrations') && (
                <Button
                  text={
                    integration.production && integration.type === 'edi' ? 'Deactivate' : 'Activate'
                  }
                  onClick={handleActivation}
                  color={ButtonColors.primary}
                  loading={activatingIntegrationId === integration.id}
                  disabled={saving}
                  className="text-sm font-medium"
                />
              )}
            </div>
          </ListItem>
        );
      })}
    </List>
  );
};

export default Integrations;
